import React, { useEffect, useState, useRef, useCallback } from "react";
import { uploadChunked } from "../lib/upload";
import { authStore } from "../lib/api";
import { toast } from "./ui/sonner";
import { Loader2, Upload, X, FolderOpen, FileText, Link2, Video, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";

const dlUrl = (api, fid) => `${api.defaults.baseURL}/files/${fid}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`;

// 「共享资料（设计部/销售）」独立分组：不计入现场评估记录，双向实时可见。
export default function SharedResources({ api, leadId, zh, canEdit = true }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkTitle, setLinkTitle] = useState("");
  const [viewer, setViewer] = useState(null);
  const [videoView, setVideoView] = useState(null);
  // 拖拽上传状态（需求 SYS-004）：设计部/销售经常直接把手机或电脑里的照片拖进来，
  // 不再强制先点「上传资料」走系统文件框；高亮提示让投放目标可见。
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const images = files.filter((a) => a.isImage && !a.isLink);
  useEffect(() => {
    if (viewer == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setViewer(null);
      else if (e.key === "ArrowLeft" && images.length > 1) setViewer((v) => (v - 1 + images.length) % images.length);
      else if (e.key === "ArrowRight" && images.length > 1) setViewer((v) => (v + 1) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewer, images.length]);
  const upRef = useRef(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/crm/leads/${leadId}/shared-files`); setFiles(data.files || []); } catch { /* */ }
  }, [api, leadId]);
  useEffect(() => { load(); }, [load]);

  const addLink = async () => {
    const url = linkUrl.trim(); if (!url) return;
    setBusy(true);
    try {
      await api.post(`/crm/leads/${leadId}/shared-links`, { url, title: linkTitle.trim() });
      toast.success(zh ? "已添加链接（双向实时可见）" : "Link added");
      setLinkUrl(""); setLinkTitle(""); setShowLink(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || (zh ? "添加失败" : "Failed")); }
    setBusy(false);
  };

  /* 上传核心：文件选择框与拖拽共用同一段逻辑（多选逐个上传），
     这样「拖进来」和「点上传」在上传接口、错误提示、刷新行为上完全一致，不出现两套代码。 */
  const uploadList = async (picked) => {
    const list = Array.from(picked || []); if (!list.length) return;
    setBusy(true);
    try {
      const items = [];
      for (const f of list) {
        const fin = await uploadChunked(api, f);
        items.push({ fileId: fin.fileId, filename: f.name, contentType: f.type });
      }
      await api.post(`/crm/leads/${leadId}/site-survey-files`, { files: items });
      toast.success(zh ? `已上传 ${items.length} 个文件（双向实时可见）` : `Uploaded ${items.length}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || (zh ? "上传失败" : "Upload failed")); }
    setBusy(false);
  };
  const onUpload = async (e) => {
    const picked = Array.from(e.target.files || []); if (!picked.length) return;
    await uploadList(picked);
    if (upRef.current) upRef.current.value = "";
  };

  /* ---- 拖拽上传（SYS-004）----
     与工程文件区同一套做法：只认「文件」拖拽，用 depth 计数避免进入子元素高亮闪烁。 */
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  const onDragEnter = (e) => { if (!hasFiles(e)) return; e.preventDefault(); dragDepth.current += 1; setDragging(true); };
  const onDragOver = (e) => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = busy ? "none" : "copy"; };
  const onDragLeave = (e) => { if (!hasFiles(e)) return; e.preventDefault(); dragDepth.current = Math.max(0, dragDepth.current - 1); if (dragDepth.current === 0) setDragging(false); };
  const onDrop = async (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault(); dragDepth.current = 0; setDragging(false);
    if (!canEdit) return;
    if (busy) { toast.info(zh ? "正在上传，请稍候" : "Upload in progress"); return; }
    await uploadList(e.dataTransfer.files);
  };
  const del = async (aid) => { try { await api.post(`/crm/leads/${leadId}/shared-files/delete`, { aid }); load(); } catch { toast.error(zh ? "删除失败" : "Failed"); } };

  return (
    <div className={`rounded-lg border bg-sky-50/50 p-3 space-y-2 transition-colors ${dragging ? "border-sky-500 ring-2 ring-sky-200" : "border-sky-200"}`} data-testid={`shared-res-${leadId}`} data-dragging={dragging ? 1 : 0}
      onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12px] font-semibold text-sky-700 inline-flex items-center gap-1"><FolderOpen className="h-3.5 w-3.5" />{zh ? "共享资料（设计部 / 销售）" : "Shared resources"} <span className="text-slate-400">({files.length})</span>{canEdit && <span className="font-normal text-[10px] text-sky-500">{zh ? "· 可拖拽上传" : "· drag & drop"}</span>}</span>
        {canEdit && (
          <div className="flex items-center gap-1.5">
            <input ref={upRef} type="file" multiple className="hidden" data-testid={`shared-res-input-${leadId}`} onChange={onUpload} accept="image/*,video/*,application/pdf,.doc,.docx,.dwg,.xls,.xlsx" />
            <button onClick={() => setShowLink((v) => !v)} data-testid={`shared-res-add-link-${leadId}`} className="inline-flex items-center gap-1 text-[11px] text-sky-700 bg-white border border-sky-300 hover:bg-sky-50 rounded px-2.5 py-1"><Link2 className="h-3 w-3" />{zh ? "添加链接" : "Add link"}</button>
            <button onClick={() => upRef.current?.click()} disabled={busy} data-testid={`shared-res-upload-${leadId}`} className="inline-flex items-center gap-1 text-[11px] text-white bg-sky-600 hover:bg-sky-700 rounded px-2.5 py-1 disabled:opacity-50">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}{zh ? "上传资料" : "Upload"}</button>
          </div>
        )}
      </div>
      {canEdit && showLink && (
        <div className="rounded-lg border border-sky-200 bg-white p-2 space-y-1.5" data-testid={`shared-res-link-form-${leadId}`}>
          <p className="text-[11px] text-slate-500 inline-flex items-center gap-1"><Video className="h-3 w-3 text-sky-600" />{zh ? "VR 全景 / 视频链接（Matterport、720云、YouTube 等）" : "VR / video link"}</p>
          <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" data-testid={`shared-res-link-url-${leadId}`} className="w-full text-[12px] rounded border border-slate-200 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-sky-300" />
          <input value={linkTitle} onChange={(e) => setLinkTitle(e.target.value)} placeholder={zh ? "标题（选填，如 客厅 VR 全景）" : "Title (optional)"} data-testid={`shared-res-link-title-${leadId}`} className="w-full text-[12px] rounded border border-slate-200 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-sky-300" />
          <div className="flex gap-1.5">
            <button onClick={addLink} disabled={busy || !linkUrl.trim()} data-testid={`shared-res-link-save-${leadId}`} className="text-[11px] text-white bg-sky-600 hover:bg-sky-700 rounded px-2.5 py-1 disabled:opacity-50">{zh ? "保存链接" : "Save"}</button>
            <button onClick={() => { setShowLink(false); setLinkUrl(""); setLinkTitle(""); }} className="text-[11px] text-slate-500 hover:text-slate-700 rounded px-2 py-1">{zh ? "取消" : "Cancel"}</button>
          </div>
        </div>
      )}
      {/* 拖拽投放提示（SYS-004）：覆盖在整个资料区上，明确告知「松手即可上传」。 */}
      {dragging && (
        <div className="rounded-lg border-2 border-dashed border-sky-400 bg-sky-100/70 py-3 flex flex-col items-center justify-center gap-1 text-sky-700 pointer-events-none" data-testid={`shared-res-drop-hint-${leadId}`}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
          <p className="text-[11px] font-semibold">{busy ? (zh ? "上传中，请稍候…" : "Uploading…") : (zh ? "松开即上传到这里（支持多选照片 / 文件）" : "Drop to upload (multiple photos / files)")}</p>
          <p className="text-[10px] text-sky-500">{zh ? "设计部 / 销售均可见" : "Visible to Design & Sales"}</p>
        </div>
      )}
      {files.length === 0 ? (
        <p className="text-[11px] text-slate-300 text-center py-1">{zh ? "暂无共享资料 —— 可直接把照片 / 文件拖到这里" : "None — drop photos / files here"}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {files.map((a) => {
            const tag = a.source === "design" ? { t: zh ? "设计部" : "Design", c: "bg-fuchsia-600" } : (a.source === "sales" ? { t: zh ? "销售" : "Sales", c: "bg-sky-600" } : null);
            if (a.isLink) {
              return (
                <span key={a.aid} className="inline-flex items-center gap-1 rounded-md bg-white border border-sky-200 pl-2 pr-1 py-1 text-[11px] text-sky-700" data-testid={`shared-res-link-${a.aid}`}>
                  <a href={a.url} target="_blank" rel="noreferrer" title={a.url} className="inline-flex items-center gap-1 hover:text-sky-900 max-w-[180px] truncate"><Video className="h-3 w-3 shrink-0" /><span className="truncate">{a.title || a.url}</span><ExternalLink className="h-2.5 w-2.5 shrink-0 opacity-60" /></a>
                  {tag && <span className={`text-[9px] px-1 rounded text-white ${tag.c}`}>{tag.t}</span>}
                  {canEdit && <button onClick={() => del(a.aid)} data-testid={`shared-res-del-${a.aid}`} className="text-slate-300 hover:text-red-500"><X className="h-3 w-3" /></button>}
                </span>
              );
            }
            if (a.isVideo) {
              return (
                <span key={a.aid} className="inline-flex items-center gap-1 rounded-md bg-white border border-violet-200 pl-2 pr-1 py-1 text-[11px] text-violet-700" data-testid={`shared-res-video-${a.aid}`}>
                  <button type="button" onClick={() => setVideoView(a)} title={a.filename} className="inline-flex items-center gap-1 hover:text-violet-900 max-w-[180px] truncate">
                    <Video className="h-3 w-3 shrink-0" /><span className="truncate">{a.filename}</span>
                  </button>
                  {tag && <span className={`text-[9px] px-1 rounded text-white ${tag.c}`}>{tag.t}</span>}
                  {canEdit && <button onClick={() => del(a.aid)} data-testid={`shared-res-del-${a.aid}`} className="text-slate-300 hover:text-red-500"><X className="h-3 w-3" /></button>}
                </span>
              );
            }
            return a.isImage ? (
              <div key={a.aid} className="relative group" title={a.by ? `${a.by}` : ""}>
                <button type="button" data-testid={`shared-res-img-${a.aid}`} onClick={() => setViewer(Math.max(0, images.findIndex((x) => x.aid === a.aid)))} title={a.filename}>
                  <img src={dlUrl(api, a.fileId)} alt={a.filename} className="h-16 w-16 object-cover rounded-md border border-slate-200 hover:opacity-80 cursor-zoom-in" />
                </button>
                {tag && <span className={`absolute bottom-0.5 left-0.5 text-[9px] px-1 rounded text-white ${tag.c}`}>{tag.t}</span>}
                {canEdit && <button onClick={() => del(a.aid)} data-testid={`shared-res-del-${a.aid}`} className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="h-2.5 w-2.5" /></button>}
              </div>
            ) : (
              <span key={a.aid} className="inline-flex items-center gap-1 rounded-md bg-white border border-slate-200 pl-2 pr-1 py-1 text-[11px] text-slate-600">
                <a href={dlUrl(api, a.fileId)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-sky-600 max-w-[150px] truncate"><FileText className="h-3 w-3 shrink-0" /><span className="truncate">{a.filename}</span></a>
                {tag && <span className={`text-[9px] px-1 rounded text-white ${tag.c}`}>{tag.t}</span>}
                {canEdit && <button onClick={() => del(a.aid)} data-testid={`shared-res-del-${a.aid}`} className="text-slate-300 hover:text-red-500"><X className="h-3 w-3" /></button>}
              </span>
            );
          })}
        </div>
      )}
      {videoView && (
        <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center" data-testid="shared-res-video-overlay" onClick={() => setVideoView(null)}>
          <button data-testid="shared-res-video-close" onClick={(e) => { e.stopPropagation(); setVideoView(null); }}
            className="mobile-safe-close absolute top-4 right-4 h-9 w-9 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center"><X className="h-5 w-5" /></button>
          <video src={dlUrl(api, videoView.fileId)} controls autoPlay className="max-h-[85vh] max-w-[90vw] rounded-lg" onClick={(e) => e.stopPropagation()} />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/90 text-xs bg-black/40 rounded-full px-3 py-1" data-testid="shared-res-video-caption">{videoView.filename}</div>
        </div>
      )}
      {viewer != null && images[viewer] && (
        <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center" data-testid="shared-res-lightbox" onClick={() => setViewer(null)}>
          <button data-testid="shared-res-lightbox-close" onClick={(e) => { e.stopPropagation(); setViewer(null); }}
            className="mobile-safe-close absolute top-4 right-4 h-9 w-9 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center"><X className="h-5 w-5" /></button>
          {images.length > 1 && (
            <button data-testid="shared-res-lightbox-prev" onClick={(e) => { e.stopPropagation(); setViewer((v) => (v - 1 + images.length) % images.length); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center"><ChevronLeft className="h-6 w-6" /></button>
          )}
          <img src={dlUrl(api, images[viewer].fileId)} alt={images[viewer].filename}
            className="max-h-[88vh] max-w-[90vw] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          {images.length > 1 && (
            <button data-testid="shared-res-lightbox-next" onClick={(e) => { e.stopPropagation(); setViewer((v) => (v + 1) % images.length); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center"><ChevronRight className="h-6 w-6" /></button>
          )}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/90 text-xs bg-black/40 rounded-full px-3 py-1" data-testid="shared-res-lightbox-caption">
            {images[viewer].filename} · {viewer + 1}/{images.length}
          </div>
        </div>
      )}
    </div>
  );
}
